import {
	DeleteOutlined,
	EditOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	FileImageOutlined,
	PlusOutlined,
	SettingOutlined,
} from "@ant-design/icons";
import {
	App,
	Badge,
	Button,
	Card,
	Empty,
	Image,
	Space,
	Table,
	Tabs,
	Tag,
	Typography,
} from "antd";
import type React from "react";
import type { RichMenuTabGroupWithRichmenus } from "../../../../shared/types/richmenu.types";

const { Title, Text } = Typography;

interface RichMenuTabGroupsListProps {
	tabGroups: RichMenuTabGroupWithRichmenus[];
	onEdit?: (tabGroup: RichMenuTabGroupWithRichmenus) => void;
	onUpdate?: (action: string, data: any) => void;
	uploadUrl: string;
	onCreateNew?: () => void;
}

export const RichMenuTabGroupsList: React.FC<RichMenuTabGroupsListProps> = ({
	tabGroups,
	onEdit,
	onUpdate,
	uploadUrl,
	onCreateNew,
}) => {
	const { message: messageApi } = App.useApp();

	// Dynamic image URL helper
	const getImageUrl = (imageName: string) => {
		return `${uploadUrl}/${imageName}`;
	};

	const defaultTabGroup = tabGroups?.find(
		(g: RichMenuTabGroupWithRichmenus) =>
			g.displayPriority === "DEFAULT" && g.status === "PUBLISHED",
	);
	const perUserTabGroup = tabGroups?.find(
		(g: RichMenuTabGroupWithRichmenus) =>
			g.displayPriority === "USER" && g.status === "PUBLISHED",
	);
	const draftTabGroups =
		tabGroups?.filter(
			(g: RichMenuTabGroupWithRichmenus) => g.status === "DRAFT",
		) || [];

	// Debug logging
	console.log("RichMenuTabGroupsList Debug:", {
		tabGroups,
		draftTabGroups: draftTabGroups,
		draftCount: draftTabGroups.length,
	});

	const handleEdit = (tabGroup: RichMenuTabGroupWithRichmenus) => {
		if (onEdit) {
			onEdit(tabGroup);
		}
	};

	const handleDelete = (_tabGroupId: number) => {
		messageApi.warning("削除機能は準備中です");
	};

	const handlePublish = (_tabGroup: RichMenuTabGroupWithRichmenus) => {
		messageApi.info("公開機能は準備中です");
	};

	const handleUnpublish = (_tabGroup: RichMenuTabGroupWithRichmenus) => {
		messageApi.info("非公開機能は準備中です");
	};

	return (
		<div style={{ background: "#f8f9fa", minHeight: "400px", padding: "0" }}>
			<div style={{ maxWidth: "100%", margin: "0 auto" }}>
				{/* Main Content Tabs */}
				<Card style={{ borderRadius: 16, overflow: "hidden" }}>
					<Tabs
						defaultActiveKey="published"
						size="large"
						style={{ margin: -24 }}
						tabBarStyle={{
							background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
							margin: 0,
							padding: "0 24px",
						}}
						items={[
							{
								key: "published",
								label: (
									<Space>
										<EyeOutlined />
										公開中
										<Badge
											count={
												defaultTabGroup && perUserTabGroup
													? 2
													: defaultTabGroup || perUserTabGroup
														? 1
														: 0
											}
											showZero
										/>
									</Space>
								),
								children: (
									<div style={{ padding: "24px 0" }}>
										{/* Published Rich Menus Table */}
										<div style={{ marginBottom: 32 }}>
											<div style={{ marginBottom: 16, padding: "0 24px" }}>
												<Title
													level={3}
													style={{ margin: 0, color: "#262626" }}
												>
													公開中のリッチメニュー
												</Title>
												<Text type="secondary" style={{ fontSize: 14 }}>
													現在アクティブなリッチメニューの一覧
												</Text>
											</div>

											{defaultTabGroup || perUserTabGroup ? (
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														gap: 24,
													}}
												>
													{[
														...(defaultTabGroup ? [defaultTabGroup] : []),
														...(perUserTabGroup ? [perUserTabGroup] : []),
													].map((tabGroup) => (
														<div
															key={tabGroup.groupId}
															style={{ margin: "0 16px" }}
														>
															{/* Rich Menu Tabs Display */}
															<div>
																{tabGroup.richmenus &&
																tabGroup.richmenus.length > 0 ? (
																	tabGroup.richmenus.length === 1 ? (
																		// Single richmenu - display directly without tabs
																		<Card
																			title={
																				tabGroup.richmenus[0].name ||
																				"Rich Menu 1"
																			}
																			style={{ borderRadius: 8 }}
																		>
																			<Table
																				dataSource={[tabGroup.richmenus[0]]}
																				pagination={false}
																				rowKey={(record) => record.id || 0}
																				showHeader={true}
																				scroll={{ x: 800 }}
																				columns={[
																					{
																						title:
																							tabGroup.displayPriority ===
																							"USER"
																								? "登録後"
																								: "登録前",
																						key: "image",
																						width: 120,
																						fixed: "left",
																						render: (record: any) => (
																							<div
																								style={{
																									width: "100%",
																									maxWidth: 120,
																									height: 75,
																									border: record.selected
																										? "2px solid #52c41a"
																										: "1px solid #e8e8e8",
																									borderRadius: 6,
																									backgroundColor: "#fafafa",
																									display: "flex",
																									alignItems: "center",
																									justifyContent: "center",
																									position: "relative",
																									overflow: "hidden",
																								}}
																							>
																								{record.imageName ? (
																									<Image
																										src={getImageUrl(
																											record.imageName,
																										)}
																										alt={
																											record.name || "Rich Menu"
																										}
																										style={{
																											width: "100%",
																											height: "100%",
																											objectFit: "cover",
																											borderRadius: 4,
																										}}
																										preview={{
																											mask: (
																												<div
																													style={{
																														color: "white",
																														fontSize: 12,
																													}}
																												>
																													拡大
																												</div>
																											),
																										}}
																									/>
																								) : (
																									<div
																										style={{
																											color: "#bbb",
																											fontSize: 12,
																											textAlign: "center",
																											display: "flex",
																											flexDirection: "column",
																											alignItems: "center",
																											gap: 4,
																										}}
																									>
																										<FileImageOutlined
																											style={{
																												fontSize: 24,
																											}}
																										/>
																										<span
																											style={{
																												fontSize: 10,
																											}}
																										>
																											画像なし
																										</span>
																									</div>
																								)}
																								{record.selected && (
																									<div
																										style={{
																											position: "absolute",
																											top: 4,
																											right: 4,
																											background: "#52c41a",
																											color: "white",
																											borderRadius: 4,
																											padding: "2px 6px",
																											fontSize: 10,
																											fontWeight: "bold",
																										}}
																									>
																										✓
																									</div>
																								)}
																							</div>
																						),
																					},
																					{
																						title: "リッチメニュー名",
																						dataIndex: "name",
																						key: "name",
																						width: 150,
																						render: (name: string) => (
																							<Typography.Text strong>
																								{name || "未設定"}
																							</Typography.Text>
																						),
																					},
																					{
																						title: "チャットバーテキスト",
																						dataIndex: "chatBarText",
																						key: "chatBarText",
																						width: 120,
																						render: (text: string) =>
																							text || "未設定",
																					},
																					{
																						title: "アクション設定",
																						key: "areaActions",
																						width: 350,
																						render: (record: any) => {
																							// Collect area actions from this specific rich menu
																							const areas = record.areas || [];
																							const areaActions = areas?.map(
																								(area: any, index: number) => ({
																									area: String.fromCharCode(
																										65 + index,
																									),
																									actionType:
																										area.action?.type ||
																										"未設定",
																									value:
																										area.action?.type === "uri"
																											? area.action?.uri
																											: area.action?.type ===
																													"message"
																												? area.action?.text
																												: area.action?.type ===
																														"postback"
																													? area.action?.data
																													: "未設定",
																								}),
																							);

																							return areaActions?.length > 0 ? (
																								<Table
																									dataSource={areaActions}
																									pagination={false}
																									showHeader={false}
																									style={{ margin: 0 }}
																									rowKey={(_, index) =>
																										index ?? 0
																									}
																									columns={[
																										{
																											title: "エリア",
																											dataIndex: "area",
																											key: "area",
																											width: 40,
																											render: (
																												_area: string,
																												_,
																												index: number,
																											) => (
																												<Text
																													style={{
																														backgroundColor:
																															"#f0f0f0",
																														padding: "2px 6px",
																														borderRadius: 4,
																														fontSize: 10,
																														fontWeight: "bold",
																													}}
																												>
																													{String.fromCharCode(
																														64 + index,
																													)}
																												</Text>
																											),
																										},
																										{
																											title: "タイプ",
																											dataIndex: "actionType",
																											key: "actionType",
																											width: 80,
																											render: (
																												type: string,
																											) => (
																												<Tag
																													color={
																														type === "uri"
																															? "blue"
																															: type ===
																																	"message"
																																? "green"
																																: type ===
																																		"postback"
																																	? "orange"
																																	: "default"
																													}
																													style={{
																														fontSize: 10,
																														margin: 0,
																													}}
																												>
																													{type}
																												</Tag>
																											),
																										},
																										{
																											title: "値",
																											dataIndex: "value",
																											key: "value",
																											ellipsis: true,
																											render: (
																												value: string,
																											) => (
																												<Text
																													style={{
																														fontSize: 11,
																														overflow: "hidden",
																														textOverflow:
																															"ellipsis",
																														whiteSpace:
																															"nowrap",
																														display: "block",
																													}}
																													title={value}
																												>
																													{value}
																												</Text>
																											),
																										},
																									]}
																								/>
																							) : (
																								<div
																									style={{
																										padding: "8px 0",
																										textAlign: "center",
																										color: "#999",
																										fontSize: 12,
																									}}
																								>
																									<Text
																										type="secondary"
																										style={{ fontSize: 11 }}
																									>
																										設定なし
																									</Text>
																									<Button
																										type="link"
																										style={{
																											fontSize: 10,
																											padding: 0,
																										}}
																									>
																										設定
																									</Button>
																								</div>
																							);
																						},
																					},
																					{
																						title: "アクション",
																						key: "actions",
																						width:
																							tabGroup.status === "DRAFT"
																								? 200
																								: 120,
																						fixed: "right",
																						render: () => (
																							<div
																								style={{
																									display: "flex",
																									gap: 4,
																									flexWrap: "wrap",
																								}}
																							>
																								<Button
																									type={
																										tabGroup.status ===
																										"PUBLISHED"
																											? "default"
																											: "primary"
																									}
																									icon={
																										tabGroup.status ===
																										"PUBLISHED" ? (
																											<EyeInvisibleOutlined />
																										) : (
																											<EyeOutlined />
																										)
																									}
																									onClick={() =>
																										tabGroup.status ===
																										"PUBLISHED"
																											? handleUnpublish(
																													tabGroup,
																												)
																											: handlePublish(tabGroup)
																									}
																								>
																									{tabGroup.status ===
																									"PUBLISHED"
																										? "非公開"
																										: "公開"}
																								</Button>
																								{tabGroup.status ===
																									"DRAFT" && (
																									<>
																										<Button
																											icon={<EditOutlined />}
																											onClick={() =>
																												handleEdit(tabGroup)
																											}
																										>
																											編集
																										</Button>
																										<Button
																											danger
																											icon={<DeleteOutlined />}
																											onClick={() =>
																												handleDelete(
																													tabGroup.groupId,
																												)
																											}
																										>
																											削除
																										</Button>
																									</>
																								)}
																							</div>
																						),
																					},
																				]}
																			/>
																		</Card>
																	) : (
																		// Multiple richmenus - display as tabs
																		<Card
																			title={`${tabGroup.name || "Unnamed Group"} (Tabbed Rich Menu)`}
																			style={{ borderRadius: 8 }}
																		>
																			<Tabs
																				items={tabGroup.richmenus.map(
																					(richMenu: any, index: number) => ({
																						key: String(richMenu.id || index),
																						label:
																							richMenu.name ||
																							`Rich Menu ${index + 1}`,
																						children: (
																							<Table
																								dataSource={[richMenu]}
																								pagination={false}
																								rowKey={(record) =>
																									record.id || index
																								}
																								showHeader={true}
																								scroll={{ x: 800 }}
																								columns={[
																									{
																										title:
																											tabGroup.displayPriority ===
																											"USER"
																												? "登録後"
																												: "登録前",
																										key: "image",
																										width: 120,
																										fixed: "left",
																										render: (record: any) => (
																											<div
																												style={{
																													width: "100%",
																													maxWidth: 120,
																													height: 75,
																													border:
																														record.selected
																															? "2px solid #52c41a"
																															: "1px solid #e8e8e8",
																													borderRadius: 6,
																													backgroundColor:
																														"#fafafa",
																													display: "flex",
																													alignItems: "center",
																													justifyContent:
																														"center",
																													position: "relative",
																													overflow: "hidden",
																												}}
																											>
																												{record.imageName ? (
																													<Image
																														src={getImageUrl(
																															record.imageName,
																														)}
																														alt={
																															record.name ||
																															"Rich Menu"
																														}
																														style={{
																															width: "100%",
																															height: "100%",
																															objectFit:
																																"cover",
																															borderRadius: 4,
																														}}
																														preview={{
																															mask: (
																																<div
																																	style={{
																																		color:
																																			"white",
																																		fontSize: 12,
																																	}}
																																>
																																	拡大
																																</div>
																															),
																														}}
																													/>
																												) : (
																													<div
																														style={{
																															color: "#bbb",
																															fontSize: 12,
																															textAlign:
																																"center",
																															display: "flex",
																															flexDirection:
																																"column",
																															alignItems:
																																"center",
																															gap: 4,
																														}}
																													>
																														<FileImageOutlined
																															style={{
																																fontSize: 24,
																															}}
																														/>
																														<span
																															style={{
																																fontSize: 10,
																															}}
																														>
																															画像なし
																														</span>
																													</div>
																												)}
																												{record.selected && (
																													<div
																														style={{
																															position:
																																"absolute",
																															top: 4,
																															right: 4,
																															background:
																																"#52c41a",
																															color: "white",
																															borderRadius: 4,
																															padding:
																																"2px 6px",
																															fontSize: 10,
																															fontWeight:
																																"bold",
																														}}
																													>
																														✓
																													</div>
																												)}
																											</div>
																										),
																									},
																									{
																										title:
																											"エリアアクション設定",
																										key: "areaActions",
																										width: 350,
																										render: (record: any) => {
																											// Same action area logic as single richmenu
																											const areas =
																												record.areas || [];
																											const areaActions =
																												areas?.map(
																													(
																														area: any,
																														areaIndex: number,
																													) => ({
																														areaIndex:
																															areaIndex + 1,
																														actionType:
																															area.action
																																?.type ||
																															"unknown",
																														actionValue:
																															area.action
																																?.uri ||
																															area.action
																																?.text ||
																															area.action
																																?.data ||
																															area.action
																																?.label ||
																															"N/A",
																														action: area.action,
																													}),
																												) || [];

																											if (
																												areaActions.length === 0
																											) {
																												return (
																													<div
																														style={{
																															display: "flex",
																															alignItems:
																																"center",
																															gap: 4,
																														}}
																													>
																														<Text
																															type="secondary"
																															style={{
																																fontSize: 11,
																															}}
																														>
																															設定なし
																														</Text>
																														<Button
																															type="link"
																															style={{
																																fontSize: 10,
																																padding: 0,
																															}}
																														>
																															設定
																														</Button>
																													</div>
																												);
																											}

																											return (
																												<Table
																													dataSource={
																														areaActions
																													}
																													pagination={false}
																													showHeader={true}
																													style={{
																														width: "100%",
																													}}
																													rowKey={(
																														record: any,
																														index,
																													) =>
																														`area-action-${index || 0}-${record.areaIndex || Math.random()}`
																													}
																													scroll={{
																														y: 120,
																														x: 250,
																													}}
																													columns={[
																														{
																															title: "エリア",
																															dataIndex:
																																"areaIndex",
																															key: "areaIndex",
																															width: 50,
																															render: (
																																index: number,
																															) => (
																																<Text
																																	style={{
																																		fontSize: 10,
																																		fontWeight:
																																			"bold",
																																	}}
																																>
																																	{String.fromCharCode(
																																		64 + index,
																																	)}
																																</Text>
																															),
																														},
																														{
																															title: "タイプ",
																															dataIndex:
																																"actionType",
																															key: "actionType",
																															width: 80,
																															render: (
																																type: string,
																															) => (
																																<Tag
																																	color={
																																		type ===
																																		"uri"
																																			? "blue"
																																			: type ===
																																					"message"
																																				? "green"
																																				: type ===
																																						"postback"
																																					? "orange"
																																					: type ===
																																							"datetimepicker"
																																						? "purple"
																																						: "default"
																																	}
																																	style={{
																																		fontSize: 9,
																																		margin: 0,
																																	}}
																																>
																																	{type}
																																</Tag>
																															),
																														},
																														{
																															title: "値",
																															dataIndex:
																																"actionValue",
																															key: "actionValue",
																															render: (
																																value: string,
																															) => (
																																<Text
																																	style={{
																																		fontSize: 10,
																																		maxWidth: 120,
																																		overflow:
																																			"hidden",
																																		textOverflow:
																																			"ellipsis",
																																		whiteSpace:
																																			"nowrap",
																																		display:
																																			"block",
																																	}}
																																	title={value}
																																>
																																	{value}
																																</Text>
																															),
																														},
																													]}
																												/>
																											);
																										},
																									},
																									{
																										title: "アクション",
																										key: "actions",
																										width:
																											tabGroup.status ===
																											"DRAFT"
																												? 200
																												: 120,
																										fixed: "right",
																										render: () => (
																											<div
																												style={{
																													display: "flex",
																													gap: 4,
																													flexWrap: "wrap",
																												}}
																											>
																												<Button
																													type={
																														tabGroup.status ===
																														"PUBLISHED"
																															? "default"
																															: "primary"
																													}
																													icon={
																														tabGroup.status ===
																														"PUBLISHED" ? (
																															<EyeInvisibleOutlined />
																														) : (
																															<EyeOutlined />
																														)
																													}
																													onClick={() =>
																														tabGroup.status ===
																														"PUBLISHED"
																															? handleUnpublish(
																																	tabGroup,
																																)
																															: handlePublish(
																																	tabGroup,
																																)
																													}
																												>
																													{tabGroup.status ===
																													"PUBLISHED"
																														? "非公開"
																														: "公開"}
																												</Button>

																												{tabGroup.status ===
																													"DRAFT" && (
																													<>
																														<Button
																															icon={
																																<EditOutlined />
																															}
																															onClick={() =>
																																handleEdit(
																																	tabGroup,
																																)
																															}
																														>
																															編集
																														</Button>
																														<Button
																															danger
																															icon={
																																<DeleteOutlined />
																															}
																															onClick={() =>
																																handleDelete(
																																	tabGroup.groupId,
																																)
																															}
																														>
																															削除
																														</Button>
																													</>
																												)}
																											</div>
																										),
																									},
																								]}
																							/>
																						),
																					}),
																				)}
																			/>
																		</Card>
																	)
																) : (
																	<Empty
																		image={Empty.PRESENTED_IMAGE_SIMPLE}
																		description="リッチメニューがありません"
																		style={{ margin: "20px 0" }}
																	/>
																)}
															</div>
														</div>
													))}
												</div>
											) : (
												<div
													style={{
														padding: "60px 24px",
														textAlign: "center",
													}}
												>
													<Empty
														image={Empty.PRESENTED_IMAGE_SIMPLE}
														description="公開中のリッチメニューはありません"
													>
														{onCreateNew && (
															<Button
																type="primary"
																icon={<PlusOutlined />}
																onClick={onCreateNew}
															>
																新しいリッチメニューを作成
															</Button>
														)}
													</Empty>
												</div>
											)}
										</div>
									</div>
								),
							},
							{
								key: "drafts",
								label: (
									<Space>
										<SettingOutlined />
										下書き
										<Badge count={draftTabGroups.length} showZero />
									</Space>
								),
								children: (
									<div style={{ padding: "24px 0" }}>
										{/* Draft Rich Menus Table */}
										<div style={{ marginBottom: 32 }}>
											<div style={{ marginBottom: 16, padding: "0 24px" }}>
												<Title
													level={3}
													style={{ margin: 0, color: "#262626" }}
												>
													下書きのリッチメニュー
												</Title>
												<Text type="secondary" style={{ fontSize: 14 }}>
													編集中のリッチメニューの一覧
												</Text>
											</div>

											{draftTabGroups.length > 0 ? (
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														gap: 24,
													}}
												>
													{draftTabGroups.map((tabGroup) => (
														<div
															key={tabGroup.groupId}
															style={{ margin: "0 16px" }}
														>
															{/* Rich Menu Tabs Display */}
															<div>
																{tabGroup.richmenus &&
																tabGroup.richmenus.length > 0 ? (
																	tabGroup.richmenus.length === 1 ? (
																		// Single richmenu - display directly without tabs
																		<Card
																			title={
																				tabGroup.richmenus[0].name ||
																				"Rich Menu 1"
																			}
																			style={{ borderRadius: 8 }}
																		>
																			<Table
																				dataSource={[tabGroup.richmenus[0]]}
																				pagination={false}
																				rowKey={(record) => record.id || 0}
																				showHeader={true}
																				scroll={{ x: 800 }}
																				columns={[
																					{
																						title:
																							tabGroup.displayPriority ===
																							"USER"
																								? "登録後"
																								: "登録前",
																						key: "image",
																						width: 120,
																						fixed: "left",
																						render: (record: any) => (
																							<div
																								style={{
																									width: "100%",
																									maxWidth: 120,
																									height: 75,
																									border: record.selected
																										? "2px solid #52c41a"
																										: "1px solid #e8e8e8",
																									borderRadius: 6,
																									backgroundColor: "#fafafa",
																									display: "flex",
																									alignItems: "center",
																									justifyContent: "center",
																									position: "relative",
																									overflow: "hidden",
																								}}
																							>
																								{record.imageName ? (
																									<Image
																										src={getImageUrl(
																											record.imageName,
																										)}
																										alt={
																											record.name || "Rich Menu"
																										}
																										style={{
																											width: "100%",
																											height: "100%",
																											objectFit: "cover",
																											borderRadius: 4,
																										}}
																										preview={{
																											mask: (
																												<div
																													style={{
																														color: "white",
																														fontSize: 12,
																													}}
																												>
																													拡大
																												</div>
																											),
																										}}
																									/>
																								) : (
																									<div
																										style={{
																											color: "#bbb",
																											fontSize: 12,
																											textAlign: "center",
																											display: "flex",
																											flexDirection: "column",
																											alignItems: "center",
																											gap: 4,
																										}}
																									>
																										<FileImageOutlined
																											style={{
																												fontSize: 24,
																											}}
																										/>
																										<span
																											style={{
																												fontSize: 10,
																											}}
																										>
																											画像なし
																										</span>
																									</div>
																								)}
																								{record.selected && (
																									<div
																										style={{
																											position: "absolute",
																											top: 4,
																											right: 4,
																											background: "#52c41a",
																											color: "white",
																											borderRadius: 4,
																											padding: "2px 6px",
																											fontSize: 10,
																											fontWeight: "bold",
																										}}
																									>
																										✓
																									</div>
																								)}
																							</div>
																						),
																					},
																					{
																						title: "リッチメニュー名",
																						dataIndex: "name",
																						key: "name",
																						width: 150,
																						render: (name: string) => (
																							<Typography.Text strong>
																								{name || "未設定"}
																							</Typography.Text>
																						),
																					},
																					{
																						title: "チャットバーテキスト",
																						dataIndex: "chatBarText",
																						key: "chatBarText",
																						width: 120,
																						render: (text: string) =>
																							text || "未設定",
																					},
																					{
																						title: "アクション設定",
																						key: "areaActions",
																						width: 350,
																						render: (record: any) => {
																							// Same logic as published section
																							const areas = record.areas || [];
																							const areaActions = areas?.map(
																								(area: any, index: number) => ({
																									area: String.fromCharCode(
																										65 + index,
																									),
																									actionType:
																										area.action?.type ||
																										"未設定",
																									value:
																										area.action?.type === "uri"
																											? area.action?.uri
																											: area.action?.type ===
																													"message"
																												? area.action?.text
																												: area.action?.type ===
																														"postback"
																													? area.action?.data
																													: "未設定",
																								}),
																							);

																							return areaActions?.length > 0 ? (
																								<Table
																									dataSource={areaActions}
																									pagination={false}
																									showHeader={false}
																									style={{ margin: 0 }}
																									rowKey={(_, index) =>
																										index ?? 0
																									}
																									columns={[
																										{
																											title: "エリア",
																											dataIndex: "area",
																											key: "area",
																											width: 40,
																											render: (
																												_area: string,
																												_,
																												index: number,
																											) => (
																												<Text
																													style={{
																														backgroundColor:
																															"#f0f0f0",
																														padding: "2px 6px",
																														borderRadius: 4,
																														fontSize: 10,
																														fontWeight: "bold",
																													}}
																												>
																													{String.fromCharCode(
																														64 + index,
																													)}
																												</Text>
																											),
																										},
																										{
																											title: "タイプ",
																											dataIndex: "actionType",
																											key: "actionType",
																											width: 80,
																											render: (
																												type: string,
																											) => (
																												<Tag
																													color={
																														type === "uri"
																															? "blue"
																															: type ===
																																	"message"
																																? "green"
																																: type ===
																																		"postback"
																																	? "orange"
																																	: "default"
																													}
																													style={{
																														fontSize: 10,
																														margin: 0,
																													}}
																												>
																													{type}
																												</Tag>
																											),
																										},
																										{
																											title: "値",
																											dataIndex: "value",
																											key: "value",
																											ellipsis: true,
																											render: (
																												value: string,
																											) => (
																												<Text
																													style={{
																														fontSize: 11,
																														overflow: "hidden",
																														textOverflow:
																															"ellipsis",
																														whiteSpace:
																															"nowrap",
																														display: "block",
																													}}
																													title={value}
																												>
																													{value}
																												</Text>
																											),
																										},
																									]}
																								/>
																							) : (
																								<div
																									style={{
																										padding: "8px 0",
																										textAlign: "center",
																										color: "#999",
																										fontSize: 12,
																									}}
																								>
																									<Text
																										type="secondary"
																										style={{ fontSize: 11 }}
																									>
																										設定なし
																									</Text>
																									<Button
																										type="link"
																										style={{
																											fontSize: 10,
																											padding: 0,
																										}}
																									>
																										設定
																									</Button>
																								</div>
																							);
																						},
																					},
																					{
																						title: "アクション",
																						key: "actions",
																						width:
																							tabGroup.status === "DRAFT"
																								? 200
																								: 120,
																						fixed: "right",
																						render: () => (
																							<div
																								style={{
																									display: "flex",
																									gap: 4,
																									flexWrap: "wrap",
																								}}
																							>
																								<Button
																									type={
																										tabGroup.status ===
																										"PUBLISHED"
																											? "default"
																											: "primary"
																									}
																									icon={
																										tabGroup.status ===
																										"PUBLISHED" ? (
																											<EyeInvisibleOutlined />
																										) : (
																											<EyeOutlined />
																										)
																									}
																									onClick={() =>
																										tabGroup.status ===
																										"PUBLISHED"
																											? handleUnpublish(
																													tabGroup,
																												)
																											: handlePublish(tabGroup)
																									}
																								>
																									{tabGroup.status ===
																									"PUBLISHED"
																										? "非公開"
																										: "公開"}
																								</Button>
																								{tabGroup.status ===
																									"DRAFT" && (
																									<>
																										<Button
																											icon={<EditOutlined />}
																											onClick={() =>
																												handleEdit(tabGroup)
																											}
																										>
																											編集
																										</Button>
																										<Button
																											danger
																											icon={<DeleteOutlined />}
																											onClick={() =>
																												handleDelete(
																													tabGroup.groupId,
																												)
																											}
																										>
																											削除
																										</Button>
																									</>
																								)}
																							</div>
																						),
																					},
																				]}
																			/>
																		</Card>
																	) : (
																		// Multiple richmenus - display as tabs (same as published)
																		<Card
																			title={`${tabGroup.name || "Unnamed Group"} (Tabbed Rich Menu)`}
																			style={{ borderRadius: 8 }}
																		>
																			<Tabs
																				items={tabGroup.richmenus.map(
																					(richMenu: any, index: number) => ({
																						key: String(richMenu.id || index),
																						label:
																							richMenu.name ||
																							`Rich Menu ${index + 1}`,
																						children: (
																							<Table
																								dataSource={[richMenu]}
																								pagination={false}
																								rowKey={(record) =>
																									record.id || index
																								}
																								showHeader={true}
																								scroll={{ x: 800 }}
																								columns={[
																									// Same columns as published tabbed view
																									{
																										title:
																											tabGroup.displayPriority ===
																											"USER"
																												? "登録後"
																												: "登録前",
																										key: "image",
																										width: 120,
																										fixed: "left",
																										render: (record: any) => (
																											<div
																												style={{
																													width: "100%",
																													maxWidth: 120,
																													height: 75,
																													border:
																														record.selected
																															? "2px solid #52c41a"
																															: "1px solid #e8e8e8",
																													borderRadius: 6,
																													backgroundColor:
																														"#fafafa",
																													display: "flex",
																													alignItems: "center",
																													justifyContent:
																														"center",
																													position: "relative",
																													overflow: "hidden",
																												}}
																											>
																												{record.imageName ? (
																													<Image
																														src={getImageUrl(
																															record.imageName,
																														)}
																														alt={
																															record.name ||
																															"Rich Menu"
																														}
																														style={{
																															width: "100%",
																															height: "100%",
																															objectFit:
																																"cover",
																															borderRadius: 4,
																														}}
																														preview={{
																															mask: (
																																<div
																																	style={{
																																		color:
																																			"white",
																																		fontSize: 12,
																																	}}
																																>
																																	拡大
																																</div>
																															),
																														}}
																													/>
																												) : (
																													<div
																														style={{
																															color: "#bbb",
																															fontSize: 12,
																															textAlign:
																																"center",
																															display: "flex",
																															flexDirection:
																																"column",
																															alignItems:
																																"center",
																															gap: 4,
																														}}
																													>
																														<FileImageOutlined
																															style={{
																																fontSize: 24,
																															}}
																														/>
																														<span
																															style={{
																																fontSize: 10,
																															}}
																														>
																															画像なし
																														</span>
																													</div>
																												)}
																												{record.selected && (
																													<div
																														style={{
																															position:
																																"absolute",
																															top: 4,
																															right: 4,
																															background:
																																"#52c41a",
																															color: "white",
																															borderRadius: 4,
																															padding:
																																"2px 6px",
																															fontSize: 10,
																															fontWeight:
																																"bold",
																														}}
																													>
																														✓
																													</div>
																												)}
																											</div>
																										),
																									},
																									{
																										title:
																											"エリアアクション設定",
																										key: "areaActions",
																										width: 350,
																										render: (record: any) => {
																											// Same logic as published tabbed view
																											const areas =
																												record.areas || [];
																											const areaActions =
																												areas?.map(
																													(
																														area: any,
																														areaIndex: number,
																													) => ({
																														areaIndex:
																															areaIndex + 1,
																														actionType:
																															area.action
																																?.type ||
																															"unknown",
																														actionValue:
																															area.action
																																?.uri ||
																															area.action
																																?.text ||
																															area.action
																																?.data ||
																															area.action
																																?.label ||
																															"N/A",
																														action: area.action,
																													}),
																												) || [];

																											if (
																												areaActions.length === 0
																											) {
																												return (
																													<div
																														style={{
																															display: "flex",
																															alignItems:
																																"center",
																															gap: 4,
																														}}
																													>
																														<Text
																															type="secondary"
																															style={{
																																fontSize: 11,
																															}}
																														>
																															設定なし
																														</Text>
																														<Button
																															type="link"
																															style={{
																																fontSize: 10,
																																padding: 0,
																															}}
																														>
																															設定
																														</Button>
																													</div>
																												);
																											}

																											return (
																												<Table
																													dataSource={
																														areaActions
																													}
																													pagination={false}
																													showHeader={true}
																													style={{
																														width: "100%",
																													}}
																													rowKey={(
																														record: any,
																														index,
																													) =>
																														`area-action-${index || 0}-${record.areaIndex || Math.random()}`
																													}
																													scroll={{
																														y: 120,
																														x: 250,
																													}}
																													columns={[
																														{
																															title: "エリア",
																															dataIndex:
																																"areaIndex",
																															key: "areaIndex",
																															width: 50,
																															render: (
																																index: number,
																															) => (
																																<Text
																																	style={{
																																		fontSize: 10,
																																		fontWeight:
																																			"bold",
																																	}}
																																>
																																	{String.fromCharCode(
																																		64 + index,
																																	)}
																																</Text>
																															),
																														},
																														{
																															title: "タイプ",
																															dataIndex:
																																"actionType",
																															key: "actionType",
																															width: 80,
																															render: (
																																type: string,
																															) => (
																																<Tag
																																	color={
																																		type ===
																																		"uri"
																																			? "blue"
																																			: type ===
																																					"message"
																																				? "green"
																																				: type ===
																																						"postback"
																																					? "orange"
																																					: type ===
																																							"datetimepicker"
																																						? "purple"
																																						: "default"
																																	}
																																	style={{
																																		fontSize: 9,
																																		margin: 0,
																																	}}
																																>
																																	{type}
																																</Tag>
																															),
																														},
																														{
																															title: "値",
																															dataIndex:
																																"actionValue",
																															key: "actionValue",
																															render: (
																																value: string,
																															) => (
																																<Text
																																	style={{
																																		fontSize: 10,
																																		maxWidth: 120,
																																		overflow:
																																			"hidden",
																																		textOverflow:
																																			"ellipsis",
																																		whiteSpace:
																																			"nowrap",
																																		display:
																																			"block",
																																	}}
																																	title={value}
																																>
																																	{value}
																																</Text>
																															),
																														},
																													]}
																												/>
																											);
																										},
																									},
																									{
																										title: "アクション",
																										key: "actions",
																										width:
																											tabGroup.status ===
																											"DRAFT"
																												? 200
																												: 120,
																										fixed: "right",
																										render: () => (
																											<div
																												style={{
																													display: "flex",
																													gap: 4,
																													flexWrap: "wrap",
																												}}
																											>
																												<Button
																													type={
																														tabGroup.status ===
																														"PUBLISHED"
																															? "default"
																															: "primary"
																													}
																													icon={
																														tabGroup.status ===
																														"PUBLISHED" ? (
																															<EyeInvisibleOutlined />
																														) : (
																															<EyeOutlined />
																														)
																													}
																													onClick={() =>
																														tabGroup.status ===
																														"PUBLISHED"
																															? handleUnpublish(
																																	tabGroup,
																																)
																															: handlePublish(
																																	tabGroup,
																																)
																													}
																												>
																													{tabGroup.status ===
																													"PUBLISHED"
																														? "非公開"
																														: "公開"}
																												</Button>

																												{tabGroup.status ===
																													"DRAFT" && (
																													<>
																														<Button
																															icon={
																																<EditOutlined />
																															}
																															onClick={() =>
																																handleEdit(
																																	tabGroup,
																																)
																															}
																														>
																															編集
																														</Button>
																														<Button
																															danger
																															icon={
																																<DeleteOutlined />
																															}
																															onClick={() =>
																																handleDelete(
																																	tabGroup.groupId,
																																)
																															}
																														>
																															削除
																														</Button>
																													</>
																												)}
																											</div>
																										),
																									},
																								]}
																							/>
																						),
																					}),
																				)}
																			/>
																		</Card>
																	)
																) : (
																	<Empty
																		image={Empty.PRESENTED_IMAGE_SIMPLE}
																		description="リッチメニューがありません"
																		style={{ margin: "20px 0" }}
																	/>
																)}
															</div>
														</div>
													))}
												</div>
											) : (
												<div
													style={{
														padding: "60px 24px",
														textAlign: "center",
													}}
												>
													<Empty
														image={Empty.PRESENTED_IMAGE_SIMPLE}
														description="下書きのリッチメニューはありません"
													>
														{onCreateNew && (
															<Button
																type="primary"
																icon={<PlusOutlined />}
																onClick={onCreateNew}
															>
																新しいリッチメニューを作成
															</Button>
														)}
													</Empty>
												</div>
											)}
										</div>
									</div>
								),
							},
						]}
					/>
				</Card>
			</div>
		</div>
	);
};
